import { useEffect, useMemo, useState } from 'react';
import { isGrpcRuntimeAvailable } from '../../../shared/api/grpc/transport';
import { type SettingInfoDto } from '../../../shared/api/pipeex/pcfSettingsClient';
import { useRevitParametersStore } from '../../../shared/stores/useRevitParametersStore';
import { useSampledParameterNamesStore } from '../../../shared/stores/useSampledParameterNamesStore';
import { normalizeMappingProjectKey } from '../../../shared/lib/mappingProjectIdentity';
import {
  scoreMappingProfilesAgainstCatalog,
  type MappingProfileProjectMatch,
} from '../lib/mappingProjectReadiness';
import { buildProfileKey } from '../lib/profile';
import { traceMappingFlow, warnMappingFlow } from '../lib/mappingTrace';
import {
  buildProfileSignature,
  resetProfileSettingsInspectionCacheForTests,
} from '../lib/project-gate/profileSettingsInspection';
import type { MappingProfileProjectCatalogSource } from '../lib/projectProfileGateTypes';
import { useProfileSettingsInspection } from './useProfileSettingsInspection';
import { useProfileFamilyDestinationSatisfaction } from './useProfileFamilyDestinationSatisfaction';

export type { MappingProfileProjectCatalogSource } from '../lib/projectProfileGateTypes';

interface UseMappingProfileProjectMatchesArgs {
  enabled: boolean;
  profiles: SettingInfoDto[];
  projectKey: string | null;
  modelVersion: number | null;
  preferredProfileKey?: string | null;
}

export interface MappingProfileProjectMatchesState {
  isLoading: boolean;
  catalogReady: boolean;
  catalogSource: MappingProfileProjectCatalogSource;
  catalogError: string | null;
  projectParameterCount: number;
  matches: MappingProfileProjectMatch[];
  topMatchProfileKey: string | null;
  exactMatchCount: number;
}

export const __resetMappingProfileProjectMatchesCacheForTests = (): void => {
  resetProfileSettingsInspectionCacheForTests();
};

function scopeMatches(
  scope: { projectKey: string | null; modelVersion: number | null },
  projectKey: string | null,
  modelVersion: number | null
): boolean {
  return scope.projectKey === projectKey && scope.modelVersion === modelVersion;
}

function isSafeExactProjectMatch(match: MappingProfileProjectMatch): boolean {
  const projectAvailableCount = match.projectAvailableCount ?? match.availableCount;
  return match.status === 'match' && match.referenceCount > 0 && projectAvailableCount === match.referenceCount;
}

export function useMappingProfileProjectMatches({
  enabled,
  profiles,
  projectKey,
  modelVersion,
  preferredProfileKey,
}: UseMappingProfileProjectMatchesArgs): MappingProfileProjectMatchesState {
  const normalizedProjectKey = useMemo(() => normalizeMappingProjectKey(projectKey), [projectKey]);
  const grpcRuntimeAvailable = isGrpcRuntimeAvailable();
  const hostRuntimeAvailable = grpcRuntimeAvailable;
  const projectParameters = useRevitParametersStore((state) => state.parameters);
  const loadedProjectKey = useRevitParametersStore((state) => state.projectKey);
  const loadedModelVersion = useRevitParametersStore((state) => state.modelVersion);
  const projectCatalogLoading = useRevitParametersStore((state) => state.isLoading);
  const projectCatalogLoaded = useRevitParametersStore((state) => state.isLoaded);
  const projectCatalogError = useRevitParametersStore((state) => state.error);
  const fetchProjectParameters = useRevitParametersStore((state) => state.fetchParameters);
  const sampledNames = useSampledParameterNamesStore((state) => state.names);
  const sampledStoreLoading = useSampledParameterNamesStore((state) => state.isLoading);
  const sampledAttempted = useSampledParameterNamesStore((state) => state.attempted);
  const sampledError = useSampledParameterNamesStore((state) => state.error);
  const sampledStoreProjectKey = useSampledParameterNamesStore((state) => state.projectKey);
  const sampledStoreModelVersion = useSampledParameterNamesStore((state) => state.modelVersion);
  const fetchSampledNames = useSampledParameterNamesStore((state) => state.fetchNames);
  const [fullInspectionAfterPreferredMissKey, setFullInspectionAfterPreferredMissKey] =
    useState<string | null>(null);
  const profilesToInspect = useMemo(() => {
    if (!preferredProfileKey || fullInspectionAfterPreferredMissKey === preferredProfileKey) {
      return profiles;
    }

    const preferredProfile = profiles.find((profile) => buildProfileKey(profile) === preferredProfileKey);
    return preferredProfile ? [preferredProfile] : profiles;
  }, [fullInspectionAfterPreferredMissKey, preferredProfileKey, profiles]);
  const profileSignature = useMemo(() => buildProfileSignature(profilesToInspect), [profilesToInspect]);
  const inspectingPreferredProfileOnly =
    Boolean(preferredProfileKey) &&
    profiles.length > 1 &&
    profilesToInspect.length === 1 &&
    buildProfileKey(profilesToInspect[0]) === preferredProfileKey;
  const projectCatalogMatches = scopeMatches(
    { projectKey: loadedProjectKey, modelVersion: loadedModelVersion },
    normalizedProjectKey,
    modelVersion
  );
  const projectCatalogErrorMatches = projectCatalogMatches || loadedProjectKey === normalizedProjectKey;
  const projectCatalogReady =
    !hostRuntimeAvailable || (projectCatalogLoaded && projectCatalogMatches);
  const sampledCatalogMatches = scopeMatches(
    { projectKey: sampledStoreProjectKey, modelVersion: sampledStoreModelVersion },
    normalizedProjectKey,
    modelVersion
  );
  const sampledCatalogRequired = grpcRuntimeAvailable && projectCatalogReady;
  const sampledCatalogReady =
    !sampledCatalogRequired ||
    (sampledCatalogMatches && (sampledAttempted || sampledError !== null));

  useEffect(() => {
    if (!enabled || !normalizedProjectKey || !hostRuntimeAvailable) {
      return;
    }

    const catalogState = useRevitParametersStore.getState();
    traceMappingFlow('project profile match parameter catalog requested', {
      projectKey,
      normalizedProjectKey,
      modelVersion,
      projectCatalogLoading: catalogState.isLoading,
      projectCatalogLoaded: catalogState.isLoaded,
      projectCatalogError: catalogState.error,
    });
    void fetchProjectParameters({
      projectKey: normalizedProjectKey,
      modelVersion,
    });
  }, [enabled, fetchProjectParameters, hostRuntimeAvailable, modelVersion, normalizedProjectKey, projectKey]);

  useEffect(() => {
    if (!enabled || !normalizedProjectKey || !grpcRuntimeAvailable) {
      return;
    }

    // The sampled names enumeration has no data dependency on the structured
    // project catalog, so it starts in parallel with it instead of waiting
    // for the catalog to finish. The store shares the result with the
    // readiness repair and profile-load confirmation consumers.
    traceMappingFlow('project profile sampled catalog fetch start', {
      projectKey,
      normalizedProjectKey,
      modelVersion,
    });
    void fetchSampledNames({
      projectKey: normalizedProjectKey,
      modelVersion,
    })
      .then((names) => {
        traceMappingFlow('project profile sampled catalog fetch complete', {
          projectKey,
          normalizedProjectKey,
          modelVersion,
          success: true,
          nameCount: names.length,
          error: null,
        });
      })
      .catch((error) => {
        warnMappingFlow('project profile sampled catalog fetch failed', {
          projectKey,
          normalizedProjectKey,
          modelVersion,
          error: error instanceof Error ? error.message : 'Failed to sample active Revit project parameters.',
        });
      });
  }, [
    enabled,
    fetchSampledNames,
    grpcRuntimeAvailable,
    modelVersion,
    normalizedProjectKey,
    projectKey,
  ]);

  const profileInspection = useProfileSettingsInspection({
    enabled,
    projectKey,
    normalizedProjectKey,
    modelVersion,
    profiles,
    profilesToInspect,
    profileSignature,
    preferredProfileKey,
  });

  const catalogReady = Boolean(normalizedProjectKey) && projectCatalogReady && sampledCatalogReady;
  const catalogSource: MappingProfileProjectCatalogSource = !normalizedProjectKey
    ? 'none'
    : !hostRuntimeAvailable
      ? 'nonHost'
      : projectCatalogReady
        ? sampledCatalogMatches && sampledNames.length > 0 && !sampledError
          ? 'project+sampled'
          : 'project'
        : 'none';

  const familySatisfaction = useProfileFamilyDestinationSatisfaction({
    enabled,
    hostRuntimeAvailable,
    normalizedProjectKey,
    modelVersion,
    profileInspection,
    profileSignature,
  });

  // A failed backend family assessment must not degrade into an "everything is
  // missing" score: report the catalog as errored so the gate blocks with the
  // backend error instead of trusting verdicts derived without family evidence.
  const catalogError =
    familySatisfaction.error ??
    (hostRuntimeAvailable && !projectCatalogReady && projectCatalogErrorMatches
      ? projectCatalogError
      : null);

  const projectParameterNames = useMemo(
    () =>
      projectCatalogMatches
        ? projectParameters.flatMap((parameter) => [parameter.name, parameter.label])
        : [],
    [projectCatalogMatches, projectParameters]
  );
  const sampledParameterNames = useMemo(
    () => sampledCatalogMatches && !sampledError ? sampledNames : [],
    [sampledError, sampledNames, sampledCatalogMatches]
  );

  const matches = useMemo(() => {
    if (
      !catalogReady ||
      !familySatisfaction.isReady ||
      profileInspection.projectKey !== normalizedProjectKey ||
      profileInspection.modelVersion !== modelVersion ||
      profileInspection.profileSignature !== profileSignature ||
      profileInspection.isLoading
    ) {
      return [];
    }

    return scoreMappingProfilesAgainstCatalog(
      profileInspection.candidates.map((candidate) => ({
        ...candidate,
        scopeRequirements:
          familySatisfaction.requirementsByProfileKey[buildProfileKey(candidate.profile)],
        familyScopeAssessments:
          familySatisfaction.assessmentsByProfileKey[buildProfileKey(candidate.profile)],
      })),
      {
        projectParameterNames,
        sampledParameterNames,
      },
      projectCatalogMatches ? projectParameters : []
    );
  }, [
    catalogReady,
    familySatisfaction.isReady,
    familySatisfaction.assessmentsByProfileKey,
    familySatisfaction.requirementsByProfileKey,
    profileInspection,
    profileSignature,
    projectParameterNames,
    projectParameters,
    projectCatalogMatches,
    sampledParameterNames,
    normalizedProjectKey,
    modelVersion,
  ]);

  const exactMatchCount = matches.filter((match) => match.status === 'match').length;
  const topMatchProfileKey = matches[0]?.profileKey ?? null;
  const catalogBlocked = Boolean(hostRuntimeAvailable && !projectCatalogReady && catalogError);
  const isLoading =
    enabled &&
    Boolean(normalizedProjectKey) &&
    !catalogBlocked &&
    ((hostRuntimeAvailable && projectCatalogLoading) ||
      (sampledCatalogRequired && !sampledCatalogReady) ||
      profileInspection.isLoading ||
      !catalogReady ||
      (!familySatisfaction.isReady && !familySatisfaction.error));

  useEffect(() => {
    if (
      !preferredProfileKey ||
      !inspectingPreferredProfileOnly ||
      !catalogReady ||
      !familySatisfaction.isReady ||
      profileInspection.isLoading ||
      profileInspection.profileSignature !== profileSignature
    ) {
      return;
    }

    const preferredMatch = matches.find((match) => match.profileKey === preferredProfileKey) ?? null;
    if (preferredMatch && isSafeExactProjectMatch(preferredMatch)) {
      return;
    }

    traceMappingFlow('project profile preferred inspection missed; expanding to all profiles', {
      projectKey,
      normalizedProjectKey,
      modelVersion,
      preferredProfileKey,
      preferredStatus: preferredMatch?.status ?? null,
      preferredReferenceCount: preferredMatch?.referenceCount ?? null,
      preferredProjectAvailableCount: preferredMatch
        ? preferredMatch.projectAvailableCount ?? preferredMatch.availableCount
        : null,
      totalProfileCount: profiles.length,
    });
    setFullInspectionAfterPreferredMissKey(preferredProfileKey);
  }, [
    catalogReady,
    familySatisfaction.isReady,
    inspectingPreferredProfileOnly,
    matches,
    modelVersion,
    normalizedProjectKey,
    preferredProfileKey,
    profileInspection.isLoading,
    profileInspection.profileSignature,
    profileSignature,
    profiles.length,
    projectKey,
  ]);

  useEffect(() => {
    traceMappingFlow('project profile match resolved', {
      projectKey,
      normalizedProjectKey,
      modelVersion,
      catalogReady,
      catalogSource,
      catalogError,
      sampledCatalogLoading: sampledStoreLoading,
      sampledCatalogAttempted: sampledAttempted,
      sampledCatalogError: sampledError,
      isLoading,
      exactMatchCount,
      topMatchProfileKey,
      matches: matches.slice(0, 6).map((match) => ({
        profileKey: match.profileKey,
        status: match.status,
        availableCount: match.availableCount,
        projectAvailableCount: match.projectAvailableCount ?? match.availableCount,
        referenceCount: match.referenceCount,
        missingCount: match.missingCount,
      })),
    });
  }, [
    catalogReady,
    catalogError,
    catalogSource,
    exactMatchCount,
    hostRuntimeAvailable,
    isLoading,
    matches,
    modelVersion,
    normalizedProjectKey,
    projectCatalogError,
    projectKey,
    sampledAttempted,
    sampledError,
    sampledStoreLoading,
    topMatchProfileKey,
  ]);

  return {
    isLoading,
    catalogReady,
    catalogSource,
    catalogError,
    projectParameterCount: projectCatalogMatches ? projectParameters.length : 0,
    matches,
    topMatchProfileKey,
    exactMatchCount,
  };
}
